import notification from "antd/es/notification";
import { all, call, put, select, takeLatest } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import Route, { TransferStopsResponse } from "@mapmycustomers/shared/types/entity/Route";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { isApiError } from "@mapmycustomers/shared/util/assert";

import messages from "@app/scene/routes/component/RouteDetails/components/View/components/MoveStopsWizard/messages";
import { callApi } from "@app/store/api/callApi";
import { openEntity } from "@app/store/entityView/actions";
import { showEntityChannel } from "@app/store/entityView/sagas";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam/selectors";
import { routeValidationErrorMessages } from "@app/store/routes/messages";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";

import { fetchDestinationRoutes, transferStops } from "./actions";
import { getMoveStopsSourceRoute } from "./selectors";
import formatMessage from "./util/formatMessage";
import getMoveStopsNotificationNode from "./util/getMoveStopsNotificationNode";
import modeAware403Message from "./util/modeAware403Message";

const DESTINATION_PAGE_SIZE = 100;

export function* onFetchDestinationRoutes({
  payload: { query },
}: ReturnType<typeof fetchDestinationRoutes.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const source: Route | undefined = yield select(getMoveStopsSourceRoute);

    const request = query
      ? {
          $filters: { includeAccessStatus: true, name: { $in: query } },
          $limit: DESTINATION_PAGE_SIZE,
          $order: "name",
        }
      : {
          $filters: { includeAccessStatus: true },
          $limit: DESTINATION_PAGE_SIZE,
          $order: "-updatedAt",
        };

    const response: ListResponse<Route> = yield callApi("fetchUnifiedRoutes", orgId, request);
    const routes = response.data.filter((route) => route.id !== source?.id);
    yield put(fetchDestinationRoutes.success(routes));
  } catch (error) {
    yield put(fetchDestinationRoutes.failure());
    yield put(handleError({ error }));
  }
}

export function* onTransferStops({ payload }: ReturnType<typeof transferStops.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: TransferStopsResponse = yield callApi("transferRouteStops", orgId, payload);

    const isMove = payload.removeFromSource;
    const skippedCount = response.warnings.filter((warning) => warning.code === "w0003").length;
    const transferred = payload.stopIds.length - skippedCount;
    const key = "move-stops-transfer";
    const onView = () => {
      notification.close(key);
      showEntityChannel.put(
        openEntity({ entityId: response.destinationRoute.id, entityType: EntityType.ROUTE })
      );
    };

    if (transferred === 0) {
      notification.info({
        duration: NOTIFICATION_DURATION_WITH_ACTION,
        key,
        message: getMoveStopsNotificationNode(
          formatMessage(isMove ? messages.allSkippedMove : messages.allSkippedCopy, {
            count: skippedCount,
            routeName: response.destinationRoute.name,
          }),
          onView
        ),
      });
    } else {
      const secondary: string[] = [];
      if (skippedCount > 0) {
        secondary.push(
          formatMessage(isMove ? messages.skippedMove : messages.skippedCopy, {
            count: skippedCount,
          })
        );
      }
      notification.success({
        duration: NOTIFICATION_DURATION_WITH_ACTION,
        key,
        message: getMoveStopsNotificationNode(
          formatMessage(isMove ? messages.movedSuccess : messages.copiedSuccess, {
            count: transferred,
            routeName: response.destinationRoute.name,
          }),
          onView,
          secondary
        ),
      });
    }

    const refetchSource = isMove && transferred > 0;
    if (payload.callback) {
      yield call(payload.callback);
    }
    yield put(
      transferStops.success({
        ...response,
        sourceRoute: refetchSource ? response.sourceRoute : null,
      })
    );
  } catch (error) {
    yield put(transferStops.failure());
    if (isApiError(error) && error.status === 409) {
      notification.error({ message: formatMessage(messages.concurrentChange) });
      return;
    }
    if (isApiError(error) && error.status === 403) {
      notification.error({ message: modeAware403Message(payload) });
      return;
    }
    yield put(
      handleError({
        defaultMessage: messages.failure,
        error,
        messageByErrorCodeMap: routeValidationErrorMessages,
      })
    );
  }
}

export function* moveStopsSaga() {
  yield all([
    takeLatest(fetchDestinationRoutes.request, onFetchDestinationRoutes),
    takeLatest(transferStops.request, onTransferStops),
  ]);
}
